import React, { useCallback, useState, type KeyboardEvent } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { css } from 'styled-components';
import InlineDialog from '@atlaskit/inline-dialog';
import InlineEdit from '@atlaskit/inline-edit';
import { colors, typography } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import TextArea from '@atlassian/jira-common-components-text-area/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useDefaultAttributeValue } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/default/index.tsx';
import { useLabelAttribute } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/store/index.tsx';
import { useEditableAttribute } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/use-editable-attribute/index.tsx';
import { getFirstValidationErrorMessage } from '@atlassian/jira-servicedesk-insight-shared-utils/src/common/utils/get-validation-error/index.tsx';
import { useStringLengthValidation } from '@atlassian/jira-servicedesk-insight-shared-utils/src/common/utils/string-length-validation/index.tsx';
import messages from './messages.tsx';

export type Props = {
	canEdit: boolean;
};

export const ObjectNameInlineEdit = (props: Props) => {
	const { formatMessage } = useIntl();
	const { canEdit } = props;
	const [labelAttribute] = useLabelAttribute();
	const [, { update }] = useEditableAttribute(labelAttribute.objectTypeAttribute);
	const [value, setValue] = useDefaultAttributeValue(labelAttribute);
	const { handleEmptyStringValidation } = useStringLengthValidation();
	const [isEditing, setIsEditing] = useState(false);
	const [serverError, setServerError] = useState<string | null>(null);
	const [originalValue, setOriginalValue] = useState(value);

	const onConfirm = useCallback(
		(updatedLabel: string) => {
			const updated = updatedLabel.trim();
			setValue(updated);
			setServerError(null);
			if (updated !== originalValue) {
				update({
					updatedValues: [{ value: updated }],
					onError: (error) => {
						setIsEditing(true);
						const errorMessage =
							getFirstValidationErrorMessage(error) ?? formatMessage(messages.unknownError);
						setServerError(errorMessage);
					},
				});
			}
			setIsEditing(false);
		},
		[formatMessage, originalValue, setValue, update],
	);

	const onEdit = () => {
		setServerError(null);
		setOriginalValue(value);
		setIsEditing(true);
	};

	const onCancel = () => {
		setServerError(null);
		setValue(originalValue);
		setIsEditing(false);
	};

	const createOnKeyDown = useCallback(
		(currentValue: string | null) => (event: KeyboardEvent<HTMLElement>) => {
			// Prevents 'enter' from inserting a newline character, and instead
			// 'submits' the inline edit. Note that this could be achieved ootb
			// with an atlaskit TextField (instead of TextArea), however there
			// are stricter styling contraints on that component which makes
			// matching the design spec impossible.
			if (event.key === 'Enter') {
				event.preventDefault();
				if (currentValue != null && currentValue !== '') {
					onConfirm(currentValue);
				} else {
					setIsEditing(false);
				}
			}
		},
		[onConfirm],
	);

	if (canEdit === false) {
		return <ReadView>{value}</ReadView>;
	}

	return (
		<InlineEdit
			isEditing={isEditing}
			onEdit={onEdit}
			onConfirm={onConfirm}
			onCancel={onCancel}
			editButtonLabel={formatMessage(messages.editButtonLabel)}
			defaultValue={value}
			readViewFitContainerWidth
			readView={() => (
				<ReadView data-test-id="servicedesk-insight-object-view.ui.header.name-inline-edit.page-title">
					{value}
				</ReadView>
			)}
			editView={({ errorMessage, ...fieldProps }) => {
				const error = serverError ?? errorMessage;
				return (
					<InlineDialog content={error} isOpen={error !== undefined} placement="right">
						<EditViewContainer isInvalid={error !== undefined}>
							<TextArea
								{...fieldProps}
								isRequired
								shouldAutoFocus
								onKeyDown={createOnKeyDown(fieldProps.value)}
								aria-label={formatMessage(messages.inlineEditLabel)}
							/>
						</EditViewContainer>
					</InlineDialog>
				);
			}}
			validate={handleEmptyStringValidation(formatMessage(messages.objectLabel))}
		/>
	);
};

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
const headerStyles = css`
	${typography.h700()};
	margin-top: 0;
	padding: ${token('space.075', '6px')};
`;

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReadView = styled.h1`
	${headerStyles};
	text-overflow: ellipsis;
	overflow: hidden;
	font-size: 24px;
	font-weight: 400;
`;

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EditViewContainer = styled.div<{ isInvalid?: boolean }>`
	${headerStyles};
	word-break: break-word;
	transition: background 0.2s;
	border-color: ${
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(props: any) =>
			props.isInvalid
				? token('color.border.danger', colors.R400)
				: token('color.border.brand', colors.B100)
	};
	border-style: solid;
	border-radius: 3px;
	border-width: 2px;
	box-sizing: border-box;
	overflow: auto;
`;
